// ANGULAR
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
// SERVICES
import { AlertService } from '@services/alert.service';
import { GlobalService } from '@services/global.service';
import { IPsService } from '@api-requests-services/ips.service';
import { AplicacionesService } from '@api-requests-services/aplicaciones.service';
// MODELS
import { Secret } from '@models/secretos.model';
import { PostPutAppConfigurationRequest, Application, IPsLog, Variable } from '@models/aplicaciones.model';
// OTHERS
import Swal from 'sweetalert2';

export enum Views {
  Home = 'home',
  Apps = 'apps',
  AppsByKey = 'appsByKey',
  AppsByID = 'appsByID',
  AppDetails = 'appDetails',
  CreatingApp = 'creatingApp',
  EditingApp = 'editingApp'
}

export enum SecondaryViews {
  AddConfiguration = 'addConfiguration',
  EditConfiguration = 'editConfiguration',
  IPsLog = 'ipsLog',
  AddSecret = 'addSecret',
  EditSecret = 'editSecret'
}

export enum IpsLogTypesEnum {
  Authorized = 'authorized',
  Unauthorized = 'unauthorized',
  Unused = 'unused'
}

@Component({
  selector: 'app-aplicaciones',
  templateUrl: './aplicaciones.component.html',
  styleUrl: './aplicaciones.component.css',
  imports: [
    FormsModule,
    CommonModule
  ]
})
export class AplicacionesComponent implements OnInit {
  allApps: Application[];
  apps: Application[];
  newApp: Application | null = null;
  appSearched: Application | null = null;
  appNameToSearch: string;
  appToSearch: string;
  ipKeyOrIp: string;
  appSelected: Application | null = null;
  ipKeys: string | undefined;
  configurations: string | undefined;
  passwordNotDisplayed: string;
  // Variables para añadir o eliminar ipKeys
  allIpKeys: string[];
  ipKeySearchToAdd: string;
  ipKeyToAdd: string;
  ipKeysToAdd: string[];
  // Variables para añadir, modificar o eliminar Configuraciones
  nameCfgToAdd: string;
  valueCfgToAdd: string;
  nameCfgToModify: string;
  valueCfgToModify: string;
  configurationsNames: string[];
  // Variables de listado de ips de log
  ipsLog: IPsLog | null = null;
  selectedLogIP: string = "";
  ipsLogTypesEnum = IpsLogTypesEnum;
  ipsLogOpen: IpsLogTypesEnum = IpsLogTypesEnum.Authorized;
  // Variables para añadir, modificar o eliminar Secretos
  nameSecretToAdd: string;
  valueSecretToAdd: string;
  nameSecretToModify: string;
  valueSecretToModify: string;
  secretsNames: string[];
  // Control de contenido html
  views = Views;
  secondaryViews = SecondaryViews;
  appNotFound: boolean = false;
  isReadOnly: boolean = true;
  showPassword: boolean = false;
  showOldPassword: boolean = false;
  currentView: string = Views.Home;
  // Variables para añadir o eliminar configuraciones o secretos en otra vista
  readonly typeCof: string = 'configuration';
  readonly typeSecret: string = 'secret';
  confSecretsOtherView: boolean = true; // Cambia el modo visual de editar o añadir configuraciones y secretos
  selectedVariable: Variable;
  newVariable: Variable = { name: '', value: '', type: '' };

  constructor(
    private alertService: AlertService,
    public globalService: GlobalService,
    private aplicacionesService: AplicacionesService,
    private ipsService: IPsService,
    private title: Title
  ) {
    this.title.setTitle('Oráculo - Aplicaciones');
  }

  ngOnInit(): void {
    this.getIpKeys();
  }

  selectView(view: string, appSelected?: Application): void {
    if (!!appSelected) { this.appSelected = appSelected; }
    this.currentView = view;

    switch (this.currentView) {
      case Views.Home:
        this.appSearched = null;
        this.appNameToSearch = '';
        this.appToSearch = '';
        this.ipKeyOrIp = '';
        this.ipKeysToAdd = [];
        this.isReadOnly = true;
        this.showPassword = false;
        this.showOldPassword = false;
        break;
      case Views.Apps:
        this.appToSearch = '';
        this.apps = this.allApps;
        this.ipKeysToAdd = [];
        this.isReadOnly = true;
        this.showPassword = false;
        this.showOldPassword = false;
        break;
      case Views.AppDetails:
        this.appToSearch = '';
        this.selectedLogIP = '';
        this.ipsLog = null;
        this.isReadOnly = true;
        this.showPassword = false;
        this.showOldPassword = false;
        if (!!this.appSelected) { this.getApp(this.appSelected.id); }
        break;
      case Views.CreatingApp:
        this.newApp = { id: '', ipCheckMode: "Disabled" };
        this.ipKeysToAdd = [];
        this.configurationsNames = [];
        this.secretsNames = [];
        this.nameCfgToModify = '';
        this.nameSecretToModify = '';
        this.isReadOnly = true;
        this.showPassword = false;
        this.showOldPassword = false;
        break;
      case Views.EditingApp:
        this.getIPsKeys();
        this.isReadOnly = false;
        this.showPassword = false;
        this.showOldPassword = false;
        break;
      case Views.AppsByKey:
        this.ipKeyOrIp = '';
        break;
      case Views.AppsByID:
        this.ipKeyOrIp = '';
        break;
    }
  }

  selectSecondaryViews(view: string, variable?: Variable): void {
    this.currentView = view;
    if (variable) { this.selectedVariable = variable; }

    switch (this.currentView) {
      case SecondaryViews.EditConfiguration:
        this.newVariable = {
          name: this.selectedVariable.name, 
          value: this.selectedVariable.value, 
          type: this.selectedVariable.type
        };
        break;
      case SecondaryViews.AddConfiguration:
        this.newVariable = { name: '', value: '', type: '' };
        break;
      case SecondaryViews.IPsLog:
        this.selectedLogIP = '';
        this.ipsLogOpen = IpsLogTypesEnum.Authorized;
        this.newVariable = { name: '', value: '', type: '' };
        break;
      case SecondaryViews.EditSecret:
        this.newVariable = {
          name: this.selectedVariable.name, 
          value: this.selectedVariable.value, 
          type: this.selectedVariable.type
        };
        break;
      case SecondaryViews.AddSecret:
        this.newVariable = { name: '', value: '', type: '' };
        break;
    }
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text)
      .then(() => console.log('Texto copiado al portapapeles'))
      .catch(err => console.error('Error al copiar:', err));
  }

  getIpKeys(): void {
    this.ipsService.getIpKeys().subscribe(
      ipKeys => {
        if (ipKeys) {
          // console.log('IpKeys: ', ipKeys);
          this.allIpKeys = ipKeys;
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting IpKeys:', error);
        }
      }
    );
  }

  getApps(changeView: boolean, isLogIP: boolean, ids?: string[]): void {
    this.aplicacionesService.getAplicaciones(ids ?? [], false).subscribe(
      applications => {
        if (applications) {
          // console.log('Apps: ', apps); 
          if (isLogIP) {
            this.showApplicationsByIp(this.selectedLogIP, applications.map(application => application.id));
          } else {
            this.allApps = applications;
            this.apps = applications;
          }

          if (changeView) {
            this.currentView = Views.Apps;
          }
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Applications:', error);
        }
      }
    );
  }

  getAppsFromIpsKey(): void {
    this.aplicacionesService.getAppsFromIpsKey(this.ipKeyOrIp).subscribe(
      ids => {
        // console.log('IDs: ', ids);
        if (ids) {
          this.getApps(true, false, ids);
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Applications IDs by key:', error);
        }
      }
    );
  }

  getAppsFromIP(isLogIP: boolean): void {
    this.aplicacionesService.getAppsFromIP(isLogIP ? this.selectedLogIP : this.ipKeyOrIp).subscribe(
      ids => {
        // console.log('IDs: ', ids);
        if (ids) {
          this.getApps(true, isLogIP, ids);
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Applications:', error);
        }
      }
    );
  }

  createApp(): void {
    if (!!this.newApp) {
      this.aplicacionesService.postCreateAplicacion(this.newApp).subscribe(
        () => {
          this.appSelected = !!this.newApp ? {...this.newApp} : null;
          this.getApps(false, false);
          this.selectView(Views.AppDetails);
          this.alertService.showAutoCloseMessage(`¡Aplicación creada!`, ``, "success", 1200);
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error creating Application:', error);
          }
        }
      );
    }
  }

  searchApp(): void {
    if (this.appNameToSearch) {
      this.aplicacionesService.getAplicacion(this.appNameToSearch, false).subscribe(
        app => {
          if (app) {
            // console.log('App: ', Apps);
            this.appNotFound = false;
            this.appSearched = app;
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            if (error.status === 404) {
              this.appNotFound = true;
            } else {
              this.appNotFound = false;
              this.alertService.swalError(error.error);
            }
            console.error('Error getting Application:', error);
          }
        }
      );
    } else {
      this.appNotFound = true;
    }
  }

  getApp(appId: string): void {
    this.aplicacionesService.getAplicacion(appId, true).subscribe(
      app => {
        if (app) {
          // console.log('App: ', Apps);
          this.newApp = app;
          this.configurationsNames = Object.keys(this.newApp?.configuraciones || {});
          this.nameCfgToModify = this.configurationsNames[0] ? this.configurationsNames[0] : '';
          this.secretsNames = Object.keys(this.newApp?.secretos || {});
          this.nameSecretToModify = this.secretsNames[0] ? this.secretsNames[0] : '';
          this.getIPsKeys();
          if (this.newApp?.password) {
            this.passwordNotDisplayed = '*****';
          } else {
            this.passwordNotDisplayed = '';
          }
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Application:', error);
        }
      }
    );
  }

  getIPsLog(): void {
    if (!!this.newApp) {
      this.aplicacionesService.getIPsLog(this.newApp.id).subscribe(
        ipsLog => {
          this.ipsLog = ipsLog;
          this.selectSecondaryViews(this.secondaryViews.IPsLog);
          if (this.ipsLog.authorized.length > 0) {
            this.ipsLogOpen = IpsLogTypesEnum.Authorized;
          } else if (this.ipsLog.unauthorized.length > 0) {
            this.ipsLogOpen = IpsLogTypesEnum.Unauthorized;
          } else if (this.ipsLog.unused.length > 0) {
            this.ipsLogOpen = IpsLogTypesEnum.Unused;
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error getting IPsLog:', error);
          }
        }
      );
    }
  }

  deleteIPsLog(): void {
    if (!!this.appSelected) {
      this.alertService.showConfirmAlert(
        `Eliminar log de IPs`,
        `¿Seguro que quieres eliminar el log de IPs de la Aplicación con id "${this.appSelected.id}"?`
      ).then((result) => {
        if (result.isConfirmed && !!this.newApp) {
          this.aplicacionesService.deleteIPsLog(this.newApp.id).subscribe(
            () => { },
            error => {
              if (error.error === this.globalService.editModeDisabledMsg) {
                this.alertService.swalEditingNotEnabled();
              } else {
                this.alertService.swalError(error.error);
                console.error('Error delete IPsLog:', error);
              }
            }
          );
        }
      });
    }
  }

  getKeysFromIP(): void {
    if (this.selectedLogIP !== '') {
      this.ipsService.getKeysFromIP(this.selectedLogIP).subscribe(
        (ipKeys) => {
          if (ipKeys) {
            this.showIPKeysByIp(this.selectedLogIP, ipKeys);
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error getting IP Key from IP:', error);
          }
        }
      );
    }
  }

  showApplicationsByIp(ip: string, applications: string[]): void {
    let content: string = '';
    applications.forEach(application => content = content + `<pre style="margin: 20px 0 0 0; font-size: 1.1rem; text-align: center;">${application}</pre>`);
    this.alertService.showHtmlMessage(`Aplicaciones con IP ${ip}`, content, 'info', false);
  }

  showIPKeysByIp(ip: string, ipKeys: string[]): void {
    let content: string = '';
    ipKeys.forEach(ipKey => content = content + `<pre style="margin: 20px 0 0 0; font-size: 1.1rem; text-align: center;">${ipKey}</pre>`);
    this.alertService.showHtmlMessage(`IP Keys con IP ${ip}`, content, 'info', false);
  }

  editApp(): void {
    if (!!this.appSelected) {
      this.alertService.showConfirmAlert(
        `Editar Aplicación`,
        `¿Seguro que quieres editar la Aplicación con id "${this.appSelected.id}"?`
      ).then((result) => {
        if (result.isConfirmed && !!this.appSelected && !!this.newApp) {
          this.aplicacionesService.putEditAplicacion(this.appSelected.id, this.newApp).subscribe(
            () => {
              this.alertService.showAutoCloseMessage(`¡Aplicación editada!`, ``, "success", 1200);
              this.getApps(true, false);
              this.selectView(Views.Apps);
            },
            error => {
              if (error.error === this.globalService.editModeDisabledMsg) {
                this.alertService.swalEditingNotEnabled();
              } else {
                this.alertService.swalError(error.error);
                console.error('Error editing Application:', error);
              }
            }
          );
        }
      });
    }
  }

  deleteApp(): void {
    if (!!this.appSelected) {
      this.alertService.showConfirmAlert(
        `Eliminar Aplicación`,
        `¿Seguro que quieres eliminar la Aplicación con id "${this.appSelected.id}"?`
      ).then((result) => {
        if (result.isConfirmed && !!this.appSelected) {
          this.aplicacionesService.deleteAplicacion(this.appSelected.id).subscribe(
            () => {
              this.alertService.showAutoCloseMessage(`¡Aplicación borrada!`, ``, "success", 1200);
              this.getApps(true, false);
            },
            error => {
              if (error.error === this.globalService.editModeDisabledMsg) {
                this.alertService.swalEditingNotEnabled();
              } else {
                this.alertService.swalError(error.error);
                console.error('Error deleting Application:', error);
              }
            }
          );
        }
      });
    }
  }

  rotatePassword(): void {
    this.alertService.showInputAlert(
      "Nueva contraseña",
      "Si no se escribe una contraseña se generara automáticamente"
    ).then((result) => {
      if (result.isConfirmed && !!this.newApp) {
        this.aplicacionesService.postRotatePasswordToApplication(this.newApp.id, result.value).subscribe(
          passwordInfo => {
            if (!!this.newApp && !!this.appSelected) {
              this.newApp.password = passwordInfo.password;
              this.appSelected.password = passwordInfo.password;
              this.newApp.passwordOld = passwordInfo.passwordOld ?? undefined;
              this.appSelected.passwordOld = passwordInfo.passwordOld ?? undefined;
              this.newApp.fechaPassword = new Date(passwordInfo.fechaPassword);
              this.appSelected.fechaPassword = new Date(passwordInfo.fechaPassword);
            }
            this.alertService.showAutoCloseMessage(`¡Contraseña cambiada!`, ``, "success", 1200);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error rotating Password:', error);
            }
          }
        );
      }
    });
  }

  revokeOldPassword(): void {
    if (this.newApp?.passwordOld) {
      this.aplicacionesService.postRotateOldPasswordToApplication(this.newApp.id).subscribe(
        () => {
          if (!!this.newApp && !!this.appSelected) {
            this.newApp.passwordOld = undefined;
            this.appSelected.passwordOld = undefined;
            this.alertService.showAutoCloseMessage(`¡Antigua contraseña revocada!`, ``, "success", 1200);
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error revoking Old password:', error);
          }
        }
      );
    } else {
      this.alertService.showAutoCloseMessage(`No hay PasswordOld`, ``, "error", 1200);
    }
  }

  getIPsKeys(): void {
    if (!!this.newApp) {
      this.aplicacionesService.getIPsKeys(this.newApp.id).subscribe(
        ipKeys => {
          // console.log('IPKeys: ', ipKeys);
          if (ipKeys) {
            if (!!this.newApp) {
              this.newApp.ipKeys = ipKeys;
              this.filterEditingIPKeys();
            }
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error adding IPKey:', error);
          }
        }
      );
    }
  }

  showIPs(): void {
    if (!!this.newApp) {
      this.aplicacionesService.getIPsKeysAll(this.newApp.id, true).subscribe(
        (ips) => {
          if (ips) {
            let html: string = '';

            let keys: any[] = Object.keys(ips);

            for (let i = 0; i < keys.length; i++) {
              const ipList: string[] = ips[keys[i]];

              if (Array.isArray(ipList)) {
                // html += `<p"><strong>${keys[i]}:</strong><p><p>${ipList.join('</p><p>')}</p>`; // En columna
                html += `<p"><strong>${keys[i]}:</strong> ${ipList.join(', ')}.</p>`; // En fila
              }
            }

            if (html === '') {
              html = 'No hay IPs';
            }

            this.alertService.showHtmlMessage(`IPs`, html, "success", false);
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error getting IPs:', error);
          }
        }
      );
    }
  }

  onSearchIpKeysChange(): void {
    this.ipKeysToAdd = this.ipKeySearchToAdd.trim() === '' ? this.allIpKeys : this.allIpKeys.filter(ipKey => ipKey.toLocaleLowerCase().includes(this.ipKeySearchToAdd.toLocaleLowerCase()));
    this.ipKeyToAdd = this.ipKeysToAdd.length > 0 ? this.ipKeysToAdd[0] : '';
  }

  addIPKey(): void {
    if (!!this.newApp) {
      this.aplicacionesService.postAddIPKeyToApplication(this.newApp.id, this.ipKeyToAdd).subscribe(
        () => {
          this.ipKeySearchToAdd = '';
          this.getIPsKeys();
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error adding IPKey:', error);
          }
        }
      );
    }
  }

  deleteIPKey(ipKeyToRemove: string): void {
    if (this.currentView === Views.EditingApp) {
      this.alertService.showConfirmAlert(
        `Eliminar IPKey`,
        `¿Seguro que quieres eliminar la IPKey "${ipKeyToRemove}"?`
      ).then((result) => {
        if (result.isConfirmed && !!this.newApp) {
          this.aplicacionesService.deleteIPKeyToApplication(this.newApp.id, ipKeyToRemove).subscribe(
            () => {
              this.getIPsKeys();
            },
            error => {
              if (error.error === this.globalService.editModeDisabledMsg) {
                this.alertService.swalEditingNotEnabled();
              } else {
                this.alertService.swalError(error.error);
                console.error('Error deleting IPKey:', error);
              }
            }
          );
        }
      });
    }
  }

  addConfiguration(): void {
    if (this.validateAndParseInputJson(this.confSecretsOtherView ? this.newVariable.value : this.valueCfgToAdd, 'add')) {
      const postPutAppConfigurationRequest: PostPutAppConfigurationRequest = {
        key: this.confSecretsOtherView ? this.newVariable.name : this.nameCfgToAdd,
        value: this.confSecretsOtherView ? this.newVariable.value : this.valueCfgToAdd
      }

      if (!!this.newApp) {
        this.aplicacionesService.postAddConfiguracionToApplication(this.newApp.id, postPutAppConfigurationRequest).subscribe(
          () => {
            this.nameCfgToAdd = '';
            this.valueCfgToAdd = '';
            this.refreshConfigurations();
            if (this.confSecretsOtherView) { this.selectView(Views.EditingApp); }
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error adding Configuration:', error);
            }
          }
        );
      }
    }
  }

  editConfiguration(): void {
    if (this.validateAndParseInputJson(this.confSecretsOtherView ? this.newVariable.value : this.valueCfgToModify, 'modify')) {
      const postPutAppConfigurationRequest: PostPutAppConfigurationRequest = {
        key: this.confSecretsOtherView ? this.newVariable.name : this.nameCfgToModify,
        value: this.confSecretsOtherView ? this.newVariable.value : this.valueCfgToModify
      }

      if (!!this.newApp) {
        this.aplicacionesService.putEditConfiguracionToApplication(this.newApp.id, postPutAppConfigurationRequest).subscribe(
          () => {
            this.valueCfgToModify = '';
            this.refreshConfigurations();
            if (this.confSecretsOtherView) { this.selectView(Views.EditingApp); }

            this.alertService.showAutoCloseMessage(`¡Configuración "${postPutAppConfigurationRequest.key}" editada!`, ``, "success", 1200);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error deleting IPKey:', error);
            }
          }
        );
      }
    }
  }

  deleteConfiguration(): void {
    this.alertService.showConfirmAlert(
      `Eliminar Configuración`,
      `¿Seguro que quieres eliminar la Configuración "${this.confSecretsOtherView ? this.newVariable.name : this.nameCfgToModify}"?`
    ).then((result) => {
      if (result.isConfirmed && !!this.newApp) {
        this.aplicacionesService.deleteConfiguracionToApplication(this.newApp.id, this.confSecretsOtherView ? this.newVariable.name : this.nameCfgToModify).subscribe(
          () => {
            this.refreshConfigurations();
            if (this.confSecretsOtherView) {
              this.selectView(Views.EditingApp);
              this.alertService.showAutoCloseMessage(`¡Configuración "${this.confSecretsOtherView ? this.newVariable.name : this.nameCfgToModify}" eliminada!`, ``, "success", 1200);
            }
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error deleting Configuration:', error);
            }
          }
        );
      }
    });
  }

  addSecret(): void {
    const secret: Secret = {
      nombre: this.confSecretsOtherView ? this.newVariable.name : this.nameSecretToAdd,
      valor: this.confSecretsOtherView ? this.newVariable.value : this.valueSecretToAdd
    }

    if (!!this.newApp) {
      this.aplicacionesService.postAddSecretoToApplication(this.newApp.id, secret).subscribe(
        () => {
          this.nameSecretToAdd = '';
          this.valueSecretToAdd = '';
          this.refreshSecrets();
          if (this.confSecretsOtherView) { this.selectView(Views.EditingApp); }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error adding Secret:', error);
          }
        }
      );
    }
  }

  editSecret(): void {
    const secret: Secret = {
      nombre: this.confSecretsOtherView ? this.newVariable.name : this.nameSecretToModify,
      valor: this.confSecretsOtherView ? this.newVariable.value : this.valueSecretToModify
    }

    if (!!this.newApp) {
      this.aplicacionesService.putSecretoToApplication(this.newApp.id, secret).subscribe(
        () => {
          this.valueSecretToModify = '';
          this.refreshSecrets();
          if (this.confSecretsOtherView) { this.selectView(Views.EditingApp); }

          this.alertService.showAutoCloseMessage(`¡Secreto "${secret.nombre}" editado!`, ``, "success", 1200);
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            this.alertService.swalError(error.error);
            console.error('Error editing Secret:', error);
          }
        }
      );
    }
  }

  deleteSecret(): void {
    this.alertService.showConfirmAlert(
      `Eliminar Secreto`,
      `¿Seguro que quieres eliminar el Secreto "${this.confSecretsOtherView ? this.newVariable.name : this.nameSecretToModify}"?`
    ).then((result) => {
      if (result.isConfirmed && !!this.newApp) {
        this.aplicacionesService.deleteSecretoToApplication(this.newApp.id, this.confSecretsOtherView ? this.newVariable.name : this.nameSecretToModify).subscribe(
          () => {
            this.refreshSecrets();
            if (this.confSecretsOtherView) {
              this.selectView(Views.EditingApp);
              this.alertService.showAutoCloseMessage(`¡Secreto "${this.confSecretsOtherView ? this.newVariable.name : this.nameSecretToModify}" eliminado!`, ``, "success", 1200);
            }
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error deleting Secret:', error);
            }
          }
        );
      }
    });
  }

  refreshConfigurations(): void {
    this.aplicacionesService.getAplicacion(this.newApp?.id ?? '', true).subscribe(
      app => {
        if (app) {
          if (!!this.newApp && !!this.appSelected) {
            this.appSelected.configuraciones = app.configuraciones;
            this.newApp.configuraciones = app.configuraciones;
            this.configurationsNames = Object.keys(this.newApp.configuraciones || {});
            this.nameCfgToModify = this.configurationsNames[0] ? this.configurationsNames[0] : '';
          }
        }

      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          if (error.status === 404) {
            this.appNotFound = true;
          } else {
            this.appNotFound = false;
            this.alertService.swalError(error.error);
          }
          console.error('Error getting Application:', error);
        }
      }
    );
  }

  getConfigurations(configuraciones: Record<string, string>): { key: string; value: string }[] {
    return Object.keys(configuraciones).map(key => ({ key: key, value: configuraciones[key] }));
  }

  refreshSecrets(): void {
    this.aplicacionesService.getAplicacion(this.newApp?.id ?? '', true).subscribe(
      app => {
        if (app) {
          if (!!this.newApp && !!this.appSelected) {
            this.appSelected.secretos = app.secretos;
            this.newApp.secretos = app.secretos;
            this.secretsNames = Object.keys(this.newApp.secretos || {});
            this.nameSecretToModify = this.secretsNames[0] ? this.secretsNames[0] : '';
          }
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          if (error.status === 404) {
            this.appNotFound = true;
          } else {
            this.appNotFound = false;
            this.alertService.swalError(error.error);
          }
          console.error('Error getting Application:', error);
        }
      }
    );
  }

  getSecrets(secretos: Record<string, string>): { key: string; value: string }[] {
    return Object.keys(secretos).map(key => ({ key: key, value: secretos[key] }));
  }

  filterEditingIPKeys(): void {
    this.ipKeysToAdd = !!this.newApp?.ipKeys ? this.allIpKeys.filter(ipKey => !this.newApp?.ipKeys?.includes(ipKey)) : this.allIpKeys;
    this.ipKeyToAdd = this.ipKeysToAdd.length > 0 ? this.ipKeysToAdd[0] : '';
  }

  onSearchChange(): void {
    this.apps = this.allApps.filter(app => app.id.toLowerCase().includes(this.appToSearch.toLowerCase()));

    if (this.appToSearch === undefined || this.appToSearch === '' || this.appToSearch === ' ') {
      this.apps = this.allApps;
    }
  }

  changeShowPassword(): void {
    if (this.newApp?.password) { this.showPassword = !this.showPassword; }
  }

  changeShowOldPassword(): void {
    if (this.newApp?.passwordOld) { this.showOldPassword = !this.showOldPassword; }
  }

  escapeHtml(text: string): string {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };

    return text.replace(/[&<>"']/g, m => map[m]);
  }

  showVarDetails(confOrSecret: { key: string, value: any }, varType: string): void {
    confOrSecret.value = this.formatObjectValueIfJson(confOrSecret.value);

    const variable: Variable = { name: confOrSecret.key, value: confOrSecret.value, type: varType };

    let content: string = this.escapeHtml(confOrSecret.value);
    if (content.trim().startsWith('{')) {
      content = `<pre style="margin: 20px 0 0 0; font-size: 1.1rem; text-align: start;">${content}</pre>`;
    } else {
      content = `<p style="margin: 20px 0 0 0; font-size: 1.4rem; text-align: start; white-space: pre-wrap;">${content}</p>`;
    }

    // Especial: showConfirmAlert sin icono y con HTML
    Swal.fire({
      title: confOrSecret.key,
      html: content,
      showConfirmButton: this.currentView === Views.EditingApp && this.confSecretsOtherView,
      confirmButtonColor: 'var(--primary-color)',
      confirmButtonText: 'Editar',
      showDenyButton: true,
      denyButtonColor: 'var(--false-color)',
      denyButtonText: 'Cerrar',
      showCancelButton: false,
      reverseButtons: true
    }).then(
      result => {
        if (result.isConfirmed) {
          if (varType === this.typeCof) {
            this.selectSecondaryViews(SecondaryViews.EditConfiguration, variable);
          } else if (varType === this.typeSecret) {
            this.selectSecondaryViews(SecondaryViews.EditSecret, variable);
          }
        }
      }
    );
  }

  formatObjectValueIfJson(value: any): any {
    if (typeof value === 'object' && value !== null) {
      try {
        const parsedJson: string = JSON.stringify(value, null, 2)
        return parsedJson;
      } catch {
        this.alertService.showAutoCloseMessage(`Formato JSON inválido de la configuración "${value}"`, ``, "error", 1200);
        return value;
      }
    } else {
      return value;
    }
  }

  validateAndParseInputJson(value: any, action: string): boolean {
    if (value.trim().startsWith("{")) {
      try {
        if (this.confSecretsOtherView) {
          this.newVariable.value = JSON.parse(value);
        } else {
          if (action === 'add') {
            this.valueCfgToAdd = JSON.parse(value);
          } else if (action === 'modify') {
            this.valueCfgToModify = JSON.parse(value);
          }
        }
        return true;
      } catch {
        this.alertService.showAutoCloseMessage(`Formato JSON inválido del valor escrito`, ``, "error", 1200);
        return false;
      }
    } else {
      return true;
    }
  }
}